import { Link } from "react-router-dom";
import { SharedHeader } from "@/components/SharedHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import { industries, industryCategories, getIconComponent } from "@/data/industries";
import { useState } from "react";

const Industries = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filteredIndustries = selectedCategory
    ? industries.filter(i => i.category === selectedCategory)
    : industries;

  return (
    <div className="min-h-screen bg-background">
      <SharedHeader showNav={false} />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">50+ Industries Served</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              AI Phone Agents for <span className="text-primary">Every Industry</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Discover how AI phone agents can transform customer communication in your industry. 
              From healthcare to hospitality, we've got you covered.
            </p>
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
            Browse Industry AI Agents
          </h2>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            <Badge 
              variant={selectedCategory === null ? "default" : "outline"}
              className="cursor-pointer px-4 py-2"
              onClick={() => setSelectedCategory(null)}
            >
              All Industries ({industries.length})
            </Badge>
            {industryCategories.map(category => {
              const count = industries.filter(i => i.category === category).length;
              return (
                <Badge 
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  className="cursor-pointer px-4 py-2"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category} ({count})
                </Badge>
              );
            })}
          </div>

          {/* Industries Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredIndustries.map((industry) => {
              const IconComponent = getIconComponent(industry.icon);
              return (
                <Link key={industry.id} to={`/industries/${industry.id}`}>
                  <Card className={`h-full hover:shadow-lg transition-all hover:scale-[1.02] bg-gradient-to-br ${industry.gradient} border-border/50`}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <IconComponent className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">{industry.name}</h3>
                          <Badge variant="outline" className="text-xs">{industry.category}</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">{industry.tagline}</p>
                      <div className="flex items-center text-primary text-sm font-medium">
                        Learn more about {industry.name} AI agents <ArrowRight className="ml-1 h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>© {new Date().getFullYear()} Call24x7.AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Industries;
